import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { getTodayCO } from '@/lib/dateUtils';
import { expirePendingPayments } from '@/services/bookingService';

export interface AppointmentRow {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string | null;
  service_id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  created_at: string;
  services: { name: string } | null;
}

export interface AppointmentFilters {
  date: string;
  status: string;
}

/** How often (ms) to run the expiry check while the admin panel is open */
const EXPIRY_POLL_INTERVAL_MS = 3 * 60 * 1000; // every 3 minutes

export function useAppointments() {
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AppointmentFilters>({ date: getTodayCO(), status: '' });
  const [cancelling, setCancelling] = useState<string | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('appointments')
        .select('id, client_name, client_email, client_phone, service_id, appointment_date, start_time, end_time, status, payment_status, created_at, services(name)')
        .order('created_at', { ascending: false });

      if (filters.date) {
        query = query.eq('appointment_date', filters.date);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setAppointments((data as AppointmentRow[]) ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al cargar las citas';
      setError(msg);
      console.error('useAppointments error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Run expiry check on mount and then every EXPIRY_POLL_INTERVAL_MS ─────────
  // This ensures that even if the Wompi webhook never arrives, pending payments
  // older than 45 minutes are expired and their slots freed automatically.
  useEffect(() => {
    // Run immediately on mount (fire-and-forget, then fetch fresh data)
    expirePendingPayments().then(() => fetchAppointments());

    // Poll every 3 minutes while the admin panel is open
    expiryTimerRef.current = setInterval(async () => {
      await expirePendingPayments();
      fetchAppointments();
    }, EXPIRY_POLL_INTERVAL_MS);

    return () => {
      if (expiryTimerRef.current) {
        clearInterval(expiryTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  /**
   * Admin cancels an appointment → status becomes 'no_show'.
   * Payment records are NEVER modified — advance payment stays as income.
   */
  const cancelAppointment = useCallback(async (id: string): Promise<boolean> => {
    setCancelling(id);
    try {
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ status: 'no_show' })
        .eq('id', id);

      if (updateError) throw updateError;

      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'no_show' } : a))
      );
      return true;
    } catch (err) {
      console.error('cancelAppointment error:', err);
      return false;
    } finally {
      setCancelling(null);
    }
  }, []);

  /**
   * Admin manually confirms a pending appointment.
   * Used when the client paid in cash/transfer and the system didn't auto-confirm.
   * Also inserts an advance payment record con amount=0 para que completeService
   * siempre encuentre el anticipo y pueda calcular el remanente correctamente.
   */
  const confirmManually = useCallback(async (id: string): Promise<boolean> => {
    setCancelling(id);
    try {
      // Verificar si ya existe un advance payment para no duplicar
      const { data: existingPayments } = await supabase
        .from('payments')
        .select('id')
        .eq('appointment_id', id)
        .eq('type', 'advance');

      // Solo insertar si no existe ninguno aún
      if (!existingPayments || existingPayments.length === 0) {
        const { error: paymentError } = await supabase
          .from('payments')
          .insert({
            appointment_id: id,
            amount: 0,
            status: 'paid',
            type: 'advance',
            method: 'cash',
          });
        if (paymentError) throw paymentError;
      }

      const { error: updateError } = await supabase
        .from('appointments')
        .update({ status: 'confirmed', payment_status: 'paid' })
        .eq('id', id);

      if (updateError) throw updateError;

      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'confirmed', payment_status: 'paid' } : a))
      );
      return true;
    } catch (err) {
      console.error('confirmManually error:', err);
      return false;
    } finally {
      setCancelling(null);
    }
  }, []);

  /**
   * Admin manually marks an appointment as 'no_show'.
   * Identical outcome to cancelAppointment — kept as a named alias
   * for semantic clarity in the UI.
   */
  const markAsNoShow = useCallback(async (id: string): Promise<boolean> => {
    setCancelling(id);
    try {
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ status: 'no_show' })
        .eq('id', id);

      if (updateError) throw updateError;

      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'no_show' } : a))
      );
      return true;
    } catch (err) {
      console.error('markAsNoShow error:', err);
      return false;
    } finally {
      setCancelling(null);
    }
  }, []);

  const [completing, setCompleting] = useState<string | null>(null);

  /**
   * Barber completes a service:
   * 1. Fetch the advance payment linked to this appointment
   * 2. Fetch the service price
   * 3. Guard: if a 'remaining' payment already exists → abort (idempotent)
   * 4. Insert a new payment record: type='remaining', method=selected, amount=price-advance, status='paid'
   * 5. Update appointment: status='completed', payment_status='paid'
   */
  const completeService = useCallback(async (
    id: string,
    method: 'cash' | 'transfer',
  ): Promise<{ ok: boolean; error?: string }> => {
    setCompleting(id);
    try {
      // 1. Fetch advance payment
      // Accept any advance status:
      // - 'paid'    → normal Wompi flow
      // - 'pending' → mock mode, expiry aún no corrió (amount=0)
      // - 'failed'  → mock mode, expiry ya corrió (amount=0)
      const { data: advancePayments, error: advErr } = await supabase
        .from('payments')
        .select('id, amount, appointment_id, status')
        .eq('appointment_id', id)
        .eq('type', 'advance')
        .in('status', ['paid', 'pending', 'failed']);

      if (advErr) throw advErr;

      // Prioridad: paid > pending > failed con amount=0
      // Supabase devuelve amount como string ("0.00"), usar Number() al comparar
      const paidAdvance = advancePayments?.find((p) => p.status === 'paid');
      const pendingAdvance = advancePayments?.find((p) => p.status === 'pending');
      const mockAdvance = advancePayments?.find((p) => p.status === 'failed' && Number(p.amount) === 0);
      const advance = paidAdvance ?? pendingAdvance ?? mockAdvance;

      // Si no hay anticipo (cita confirmada manualmente antes del fix) → tratar como $0
      const advanceAmount = advance ? Number(advance.amount) : 0;

      // 2. Guard: check if 'remaining' payment already exists
      const { data: existingRemaining, error: remErr } = await supabase
        .from('payments')
        .select('id')
        .eq('appointment_id', id)
        .eq('type', 'remaining');

      if (remErr) throw remErr;
      if (existingRemaining && existingRemaining.length > 0) {
        return { ok: false, error: 'Este servicio ya fue completado anteriormente.' };
      }

      // 3. Fetch service price via appointment → service_id
      const { data: apptData, error: apptErr } = await supabase
        .from('appointments')
        .select('service_id, services(price)')
        .eq('id', id)
        .maybeSingle();

      if (apptErr) throw apptErr;
      if (!apptData) return { ok: false, error: 'No se encontró la cita.' };

      const servicePrice = (apptData.services as { price: number } | null)?.price ?? 0;
      const remaining = Math.max(0, servicePrice - advanceAmount);

      // 4. Insert remaining payment
      const { error: insertErr } = await supabase
        .from('payments')
        .insert({
          appointment_id: id,
          type: 'remaining',
          method,
          amount: remaining,
          status: 'paid',
        });

      if (insertErr) throw insertErr;

      // 5. Update appointment status
      const { error: updateErr } = await supabase
        .from('appointments')
        .update({ status: 'completed', payment_status: 'paid' })
        .eq('id', id);

      if (updateErr) throw updateErr;

      setAppointments((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, status: 'completed', payment_status: 'paid' } : a
        )
      );

      return { ok: true };
    } catch (err) {
      console.error('completeService error:', err);
      const msg = err instanceof Error ? err.message : 'Error al completar el servicio';
      return { ok: false, error: msg };
    } finally {
      setCompleting(null);
    }
  }, []);

  const updateFilters = useCallback((newFilters: Partial<AppointmentFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ date: '', status: '' });
  }, []);

  return {
    appointments,
    loading,
    error,
    filters,
    cancelling,
    completing,
    updateFilters,
    clearFilters,
    refetch: fetchAppointments,
    cancelAppointment,
    markAsNoShow,
    confirmManually,
    completeService,
  };
}